import type { LocaleCode } from "./i18n";

type PricingTierCopy = {
  name: string;
  promise: string;
  description: string;
  action: string;
  badge?: string;
};

type RenewalEntryCopy = {
  pricing: {
    eyebrow: string;
    title: string;
    description: string;
    scopeNote: string;
    tiers: readonly [
      PricingTierCopy,
      PricingTierCopy,
      PricingTierCopy,
      PricingTierCopy,
    ];
    range: {
      eyebrow: string;
      title: string;
      description: string;
      loading: string;
      monthly: string;
      yearly: string;
      close: string;
      unavailable: string;
    };
    questions: readonly [
      { question: string; answer: string },
      { question: string; answer: string },
      { question: string; answer: string },
    ];
  };
  docs: {
    searchPlaceholder: string;
    searchLabel: string;
    groups: readonly [
      { title: string; links: readonly [string, string, string] },
      { title: string; links: readonly [string, string, string] },
      { title: string; links: readonly [string, string, string, string] },
    ];
    kicker: string;
    title: string;
    descriptionBefore: string;
    descriptionAction: string;
    descriptionAfter: string;
    steps: readonly [
      { title: string; description: string; action: string },
      { title: string; description: string; action: string },
      {
        title: string;
        description: string;
        command: string;
        followUp: string;
      },
      { title: string; description: string; action: string },
    ];
    nextLabel: string;
    nextTitle: string;
    nextDescription: string;
    nextAction: string;
    onThisPage: string;
    toc: readonly [string, string, string, string];
    noResults: string;
  };
  auth: {
    loginTitle: string;
    loginDescription: string;
    signupTitle: string;
    signupDescription: string;
    pendingLabel: string;
    pendingDescription: string;
    savedRequest: string;
    google: string;
    github: string;
    or: string;
    email: string;
    password: string;
    rotateTitle: string;
    rotateDescription: string;
    rotateNewPassword: string;
    rotateConfirmPassword: string;
    rotateMismatch: string;
    rotateSubmit: string;
    processing: string;
    continueEmail: string;
    createAccount: string;
    newQuestion: string;
    existingQuestion: string;
    tryFirst: string;
    signIn: string;
    accountNote: string;
    /**
     * The sign-in footer on `VITE_STUDIO_DISTRIBUTION=appliance`, which
     * REPLACES the `newQuestion` + `tryFirst` + `accountNote` sentence rather
     * than trimming a word out of it.
     *
     * All three of those describe a cloud flow the appliance does not have:
     * `tryFirst` navigates to `/showroom`, a page a closed-network install
     * does not serve (measured live 2026-08-19: the appliance's own sign-in
     * screen offered it); `newQuestion` and `accountNote` describe self-serve
     * signup, and on an appliance an account is issued by an administrator —
     * `inviteApplianceUser` hands out an initial password, and open signup is
     * rendered as a MISCONFIGURATION alert
     * (`appliance-admin-page.tsx` → `copy.signupOpenWarning`), not as the way
     * in. Removing only the link would leave two-thirds of a false sentence.
     *
     * Deliberately says nothing about signup, previews or account creation:
     * the smallest thing that is true here.
     */
    report: {
      kicker: string;
      beforePrimary: string;
      primary: string;
      between: string;
      secondary: string;
      afterSecondary: string;
      approve: string;
      review: string;
      replyPlaceholder: string;
      metrics: readonly [string, string, string];
    };
  };
};

const ko: RenewalEntryCopy = {
  pricing: {
    eyebrow: "결과의 깊이에 맞는 시작",
    title: "마음에 든 다음에 시작하세요",
    description:
      "모든 상품은 무료 미리보기에서 시작합니다. 내 것으로 바꾸기로 했을 때, 범위에 맞는 가격을 그 자리에서 확인합니다.",
    scopeNote:
      "Preview는 무료입니다. Ready는 월 US$9부터, Tailored는 월 US$29부터이며 모델 사용량과 최종 범위는 동의 전에 확인합니다.",
    tiers: [
      {
        name: "Preview",
        promise: "언제나 무료",
        description:
          "미리보기 후보를 살펴보고, 내 자료에 어울리는지 확인합니다. 가입도 필요 없습니다.",
        action: "미리보기 열기",
      },
      {
        name: "Ready",
        promise: "미리보기 후보를 내 것으로",
        description:
          "살펴본 제품 후보에 내 브랜드와 자료를 적용합니다. 공개 전에는 필요한 검증 근거를 다시 확인합니다.",
        action: "상품 보기",
      },
      {
        name: "Tailored",
        promise: "내 업무에 꼭 맞게",
        description:
          "데이터·규칙·화면 일부를 내 방식으로 바꿉니다. 범위를 고르면 무엇이 달라지는지 먼저 보여 드립니다.",
        action: "맞춤 범위 선택",
        badge: "대부분 여기서 시작합니다",
      },
      {
        name: "Autopilot",
        promise: "출시 후 운영 준비",
        description:
          "출시 후 운영·변경·개선에 필요한 범위를 미리 정합니다. 현재 이 옵션은 준비 중입니다.",
        action: "운영 범위 살펴보기",
        badge: "곧 열립니다",
      },
    ],
    range: {
      eyebrow: "인증된 범위 선택",
      title: "운영 범위와 결제 주기를 선택하세요",
      description:
        "여기부터는 로그인한 계정에만 실제 결제 금액을 표시합니다. 선택한 금액 그대로 동의 화면과 결제로 이어집니다.",
      loading: "확정 금액을 확인하고 있습니다…",
      monthly: "월 단위로 시작",
      yearly: "연 단위로 시작",
      close: "범위 닫기",
      unavailable: "현재 선택할 수 있는 유료 범위가 없습니다.",
    },
    questions: [
      {
        question: "미리보기에도 돈이 드나요?",
        answer: "아니요. 결과를 확인하는 일은 언제나 무료입니다.",
      },
      {
        question: "시작한 뒤 더 내야 하는 일이 생기나요?",
        answer:
          "선택한 범위와 결제 주기의 금액을 동의 전에 다시 확인합니다. 범위를 바꾸기 전에는 임의로 늘어나지 않습니다.",
      },
      {
        question: "Showroom에 없는 것도 되나요?",
        answer:
          "네. 필요한 결과를 말로 설명하면 Tailored 범위에서 함께 설계합니다.",
      },
    ],
  },
  docs: {
    searchPlaceholder: "무엇을 하고 싶으세요?",
    searchLabel: "할 수 있는 일 검색",
    groups: [
      {
        title: "시작하기",
        links: ["5분 만에 첫 제품 열기", "내 자료 가져오기", "말로 바꾸는 법"],
      },
      {
        title: "운영하기",
        links: ["Studio 사용법", "확인 요청 다루기", "운영 보고 읽는 법"],
      },
      {
        title: "깊이 알기",
        links: [
          "하나의 의미 모델",
          "검증과 증거 기록",
          "내 데이터와 보안",
          "API · 연동",
        ],
      },
    ],
    kicker: "시작하기",
    title: "5분 만에 첫 제품 열기",
    descriptionBefore: "읽기만 하는 문서가 아닙니다. 각 단계의 ",
    descriptionAction: "미리보기 열기",
    descriptionAfter: "를 누르면 안내형 미리보기에서 그 단계가 열립니다.",
    steps: [
      {
        title: "Showroom에서 결과 고르기",
        description:
          "업종이 아니라 원하는 결과로 찾으세요. 공개된 항목은 안내형 미리보기와 증거 상태를 확인할 수 있습니다.",
        action: "미리보기 열기",
      },
      {
        title: "고객과 운영자 화면 모두 써 보기",
        description:
          "두 화면을 오가며 전체 흐름을 확인하세요. 무료 Preview에는 가입이 필요 없습니다.",
        action: "미리보기 열기",
      },
      {
        title: "내 자료를 넣고 말로 바꾸기",
        description:
          "CSV·Notion·문서를 가져오고, 바꾸고 싶은 결과를 한 문장으로 말하세요.",
        command: "새 멤버에게 첫 주 환영 여정을 만들어 줘",
        followUp:
          "Coogo가 영향을 받는 범위를 먼저 보여 드립니다. 내용을 확인한 뒤 변경을 반영하세요.",
      },
      {
        title: "확인하고 발행하기",
        description:
          "App·Design·Guide·Insights가 함께 준비됩니다. 검증 결과를 보고 승인한 범위만 발행하세요.",
        action: "Studio 열기",
      },
    ],
    nextLabel: "다음 문서",
    nextTitle: "Studio 사용법",
    nextDescription: "Studio에서 발행 후 운영 상태와 후속 작업을 확인하는 방법을 알아보세요.",
    nextAction: "다음",
    onThisPage: "이 페이지에서",
    toc: ["결과 고르기", "양쪽 화면 써 보기", "말로 바꾸기", "확인하고 발행"],
    noResults: "이 목표와 일치하는 문서를 찾지 못했습니다.",
  },
  auth: {
    loginTitle: "Studio에 로그인하세요",
    loginDescription:
      "Studio는 앱을 만들고 운영하는 작업 공간입니다. 저장한 프로젝트와 운영 상태를 이어서 확인하세요.",
    signupTitle: "Coogo Studio 시작하기",
    signupDescription:
      "Coogo Studio는 앱을 만들고 운영하는 작업 공간입니다. 첫 프로젝트를 시작하세요.",
    pendingLabel: "기다리는 요청이 있습니다",
    pendingDescription: "로그인하면 이 요청을 같은 작업 페이지에서 이어갑니다.",
    savedRequest: "저장된 요청",
    google: "Google로 계속하기",
    github: "GitHub로 계속하기",
    or: "또는",
    email: "이메일 주소",
    password: "비밀번호",
    rotateTitle: "새 비밀번호를 설정하세요",
    rotateDescription:
      "설치 시 어플라이언스가 발급한 비밀번호를 사용 중입니다. 계속하려면 직접 정한 비밀번호로 바꾸세요.",
    rotateNewPassword: "새 비밀번호",
    rotateConfirmPassword: "새 비밀번호 확인",
    rotateMismatch: "비밀번호가 일치하지 않습니다.",
    rotateSubmit: "저장하고 계속하기",
    processing: "처리 중…",
    continueEmail: "이메일로 계속하기",
    createAccount: "계정 만들기",
    newQuestion: "처음이신가요?",
    existingQuestion: "이미 계정이 있나요?",
    tryFirst: "가입 없이 미리보기부터 열어 보세요",
    signIn: "로그인",
    accountNote: "계정은 결과를 저장할 때 만듭니다.",
    report: {
      kicker: "미리보기 — 운영 보고 예시",
      beforePrimary: "실측 검증 전에는 ",
      primary: "운영 수치",
      between: "와 ",
      secondary: "검증 상태",
      afterSecondary: "를 표시하지 않습니다. 연결 후 실제 기록만 보여드립니다.",
      approve: "미리보기 계속",
      review: "증거 기준 보기",
      replyPlaceholder: "답장을 입력하세요…",
      metrics: [
        "고객 지표 · 연결 대기",
        "검증 receipt · 연결 대기",
        "site · emails · 여정 — preview",
      ],
    },
  },
};

const en: RenewalEntryCopy = {
  pricing: {
    eyebrow: "Choose the depth of the outcome",
    title: "Start after you know it fits",
    description:
      "Every product starts with a free preview. When you decide to make it yours, choose the scope and see the exact price in place.",
    scopeNote:
      "Preview is free. Ready starts at US$9/month and Tailored at US$29/month; model use and the final scope are shown before consent.",
    tiers: [
      {
        name: "Preview",
        promise: "Always free",
        description:
          "Explore the preview candidate and check whether it fits your material. No account required.",
        action: "Open preview",
      },
      {
        name: "Ready",
        promise: "A preview candidate, ready to adapt",
        description:
          "Apply your brand and data to the candidate you explored. Required evidence is checked again before publication.",
        action: "View products",
      },
      {
        name: "Tailored",
        promise: "Fit to your operation",
        description:
          "Adapt selected data, rules, and screens. See the impact before you approve the scope.",
        action: "Choose a scope",
        badge: "Most teams start here",
      },
      {
        name: "Autopilot",
        promise: "Keep it operating",
        description:
          "coogo handles ongoing operation, change, and improvement while you approve the important scope.",
        action: "Delegate operations",
        badge: "Coming soon",
      },
    ],
    range: {
      eyebrow: "Authenticated scope selection",
      title: "Choose an operating scope and billing period",
      description:
        "From here, exact payment amounts are shown only to the signed-in account and carry unchanged into consent and checkout.",
      loading: "Loading confirmed amounts…",
      monthly: "Start monthly",
      yearly: "Start yearly",
      close: "Close scope",
      unavailable: "No paid scopes are currently available.",
    },
    questions: [
      {
        question: "Does the preview cost anything?",
        answer: "No. Checking the result is always free.",
      },
      {
        question: "Can the amount grow after I start?",
        answer:
          "You review the selected scope and billing period before consent. It does not change unless you change the scope.",
      },
      {
        question: "Can you build something outside the Showroom?",
        answer:
          "Yes. Describe the outcome and we will shape it together as a Tailored scope.",
      },
    ],
  },
  docs: {
    searchPlaceholder: "What would you like to do?",
    searchLabel: "Search by outcome",
    groups: [
      {
        title: "GET STARTED",
        links: [
          "Open your first product in 5 minutes",
          "Bring your material",
          "Change it in words",
        ],
      },
      {
        title: "OPERATE",
        links: [
          "Use the cockpit",
          "Handle approval requests",
          "Read voyage reports",
        ],
      },
      {
        title: "GO DEEPER",
        links: [
          "One semantic model",
          "Verification and evidence",
          "Your data and security",
          "API and integrations",
        ],
      },
    ],
    kicker: "GET STARTED",
    title: "Open your first product in 5 minutes",
    descriptionBefore: "This is not documentation you only read. Select ",
    descriptionAction: "Open preview",
    descriptionAfter: " to open that step in the guided preview.",
    steps: [
      {
        title: "Choose an outcome in the Showroom",
        description:
          "Search by the outcome you want, not by industry. Each published entry exposes its guided preview and evidence state.",
        action: "Open preview",
      },
      {
        title: "Use both customer and operator views",
        description:
          "Move through both sides of the workflow. The free Preview does not require an account.",
        action: "Open preview",
      },
      {
        title: "Bring your material and change it in words",
        description:
          "Bring CSV, Notion, or documents, then state the outcome you want in one sentence.",
        command: "Create a first-week welcome journey for new members",
        followUp:
          "coogo shows the affected scope first. Nothing changes until you approve it.",
      },
      {
        title: "Review and publish",
        description:
          "App, Design, Guide, and Status are prepared together. Publish only the scope you approve after verification.",
        action: "Open Studio",
      },
    ],
    nextLabel: "Next guide",
    nextTitle: "Using the cockpit",
    nextDescription: "See how coogo continues operating after publication.",
    nextAction: "Next",
    onThisPage: "ON THIS PAGE",
    toc: [
      "Choose an outcome",
      "Try both views",
      "Change it in words",
      "Review and publish",
    ],
    noResults: "No journey matches that outcome yet.",
  },
  auth: {
    loginTitle: "Return to the cockpit",
    loginDescription: "Your product kept operating while you were away.",
    signupTitle: "Open a new cockpit",
    signupDescription:
      "Connect your first request and source material to an outcome that can keep operating.",
    pendingLabel: "Your request is waiting",
    pendingDescription:
      "Sign in and coogo will carry this request into the same working page.",
    savedRequest: "Saved request",
    google: "Continue with Google",
    github: "Continue with GitHub",
    or: "or",
    email: "Email address",
    password: "Password",
    rotateTitle: "Choose your own password",
    rotateDescription:
      "This account still holds the password the appliance issued during installation. Replace it to continue.",
    rotateNewPassword: "New password",
    rotateConfirmPassword: "Confirm new password",
    rotateMismatch: "Passwords do not match.",
    rotateSubmit: "Save and continue",
    processing: "Working…",
    continueEmail: "Continue with email",
    createAccount: "Create account",
    newQuestion: "New to coogo?",
    existingQuestion: "Already have an account?",
    tryFirst: "Open the preview without signing up",
    signIn: "Sign in",
    accountNote: "Create an account when you save the result.",
    report: {
      kicker: "Preview — example operations report",
      beforePrimary: "Until measured verification is connected, ",
      primary: "operating metrics",
      between: " and ",
      secondary: "verification status",
      afterSecondary:
        " stay hidden. Only real records appear after connection.",
      approve: "Continue preview",
      review: "See the evidence rule",
      replyPlaceholder: "Write a reply…",
      metrics: [
        "customer metrics · awaiting connection",
        "verification receipt · awaiting connection",
        "site · emails · journey — preview",
      ],
    },
  },
};

const ja: RenewalEntryCopy = {
  pricing: {
    eyebrow: "成果の深さで選ぶ",
    title: "気に入ってから始めましょう",
    description:
      "すべての商品は無料プレビューから始まります。自分用にすると決めたら、範囲を選び、その場で正確な料金を確認できます。",
    scopeNote:
      "Preview は無料です。Ready は月額 US$9 から、Tailored は月額 US$29 から。モデル利用量と最終範囲は同意前に確認できます。",
    tiers: [
      {
        name: "Preview",
        promise: "いつでも無料",
        description:
          "プレビュー候補を確認し、自分の資料に合うか確かめます。アカウントは不要です。",
        action: "プレビューを開く",
      },
      {
        name: "Ready",
        promise: "プレビュー候補を自分向けに調整",
        description:
          "確認した候補にブランドと資料を反映します。公開前に必要な検証根拠を再確認します。",
        action: "商品を見る",
      },
      {
        name: "Tailored",
        promise: "業務にぴったり合わせる",
        description:
          "データ・ルール・画面の一部を調整し、承認前に影響範囲を確認します。",
        action: "範囲を選ぶ",
        badge: "多くのチームはここから",
      },
      {
        name: "Autopilot",
        promise: "動き続けるように",
        description:
          "公開後の運用・変更・改善を coogo が担い、重要な範囲だけ確認します。",
        action: "運用を任せる",
        badge: "近日公開",
      },
    ],
    range: {
      eyebrow: "認証済み範囲選択",
      title: "運用範囲と支払期間を選択",
      description:
        "ここからはログイン中のアカウントだけに実際の金額を表示し、その金額のまま同意と決済へ進みます。",
      loading: "確定金額を確認中…",
      monthly: "月単位で開始",
      yearly: "年単位で開始",
      close: "範囲を閉じる",
      unavailable: "現在選択できる有料範囲はありません。",
    },
    questions: [
      {
        question: "プレビューに料金はかかりますか？",
        answer: "いいえ。成果を確認することはいつでも無料です。",
      },
      {
        question: "開始後に金額が増えますか？",
        answer:
          "同意前に選択範囲と支払期間を再確認します。範囲を変えない限り勝手に増えません。",
      },
      {
        question: "Showroom にないものも作れますか？",
        answer:
          "はい。必要な成果を説明すれば Tailored の範囲として一緒に設計します。",
      },
    ],
  },
  docs: {
    searchPlaceholder: "何をしたいですか？",
    searchLabel: "成果から検索",
    groups: [
      {
        title: "はじめる",
        links: ["5分で最初の製品を開く", "資料を取り込む", "言葉で変える"],
      },
      {
        title: "運用する",
        links: ["コックピットの使い方", "確認依頼を扱う", "航海レポートを読む"],
      },
      {
        title: "深く知る",
        links: [
          "ひとつの意味モデル",
          "検証と証拠記録",
          "データとセキュリティ",
          "API・連携",
        ],
      },
    ],
    kicker: "はじめる",
    title: "5分で最初の製品を開く",
    descriptionBefore: "読むだけの文書ではありません。各手順の「",
    descriptionAction: "プレビューを開く",
    descriptionAfter: "」でガイド付きプレビューの手順を開けます。",
    steps: [
      {
        title: "Showroom で成果を選ぶ",
        description:
          "業界ではなく欲しい成果で探します。公開項目ではガイド付きプレビューと証拠状態を確認できます。",
        action: "プレビューを開く",
      },
      {
        title: "顧客と運営者の両画面を試す",
        description:
          "両側を行き来して全体の流れを確認します。無料 Preview は登録不要です。",
        action: "プレビューを開く",
      },
      {
        title: "資料を入れて言葉で変える",
        description:
          "CSV・Notion・文書を取り込み、欲しい成果を一文で伝えます。",
        command: "新しい会員向けに最初の一週間の歓迎ジャーニーを作って",
        followUp:
          "coogo は影響範囲を先に表示します。承認するまで反映されません。",
      },
      {
        title: "確認して公開する",
        description:
          "App・Design・Guide・Insights が一緒に準備されます。検証後、承認した範囲だけ公開します。",
        action: "Studio を開く",
      },
    ],
    nextLabel: "次の文書",
    nextTitle: "コックピットの使い方",
    nextDescription: "公開後も coogo が運用を続ける仕組みを確認します。",
    nextAction: "次へ",
    onThisPage: "このページ",
    toc: ["成果を選ぶ", "両画面を試す", "言葉で変える", "確認して公開"],
    noResults: "その目的に合う文書はまだありません。",
  },
  auth: {
    loginTitle: "コックピットに戻りましょう",
    loginDescription: "離れている間も製品は動き続けていました。",
    signupTitle: "新しいコックピットを開く",
    signupDescription: "最初の依頼と資料を、運用し続けられる成果につなげます。",
    pendingLabel: "待機中の依頼があります",
    pendingDescription: "ログインすると同じ作業ページで依頼を引き継ぎます。",
    savedRequest: "保存された依頼",
    google: "Google で続ける",
    github: "GitHub で続ける",
    or: "または",
    email: "メールアドレス",
    password: "パスワード",
    rotateTitle: "新しいパスワードを設定してください",
    rotateDescription:
      "インストール時にアプライアンスが発行したパスワードを使用中です。続けるにはご自身のパスワードに変更してください。",
    rotateNewPassword: "新しいパスワード",
    rotateConfirmPassword: "新しいパスワード（確認）",
    rotateMismatch: "パスワードが一致しません。",
    rotateSubmit: "保存して続行",
    processing: "処理中…",
    continueEmail: "メールで続ける",
    createAccount: "アカウントを作成",
    newQuestion: "初めてですか？",
    existingQuestion: "アカウントをお持ちですか？",
    tryFirst: "登録せずプレビューを開く",
    signIn: "ログイン",
    accountNote: "成果を保存するときにアカウントを作成します。",
    report: {
      kicker: "プレビュー — 運用レポート例",
      beforePrimary: "実測の検証が接続されるまでは、",
      primary: "運用指標",
      between: "と",
      secondary: "検証状態",
      afterSecondary: "を表示しません。接続後は実際の記録だけを表示します。",
      approve: "プレビューを続ける",
      review: "証拠ルールを見る",
      replyPlaceholder: "返信を入力…",
      metrics: [
        "顧客指標・接続待ち",
        "検証 receipt・接続待ち",
        "site・emails・journey — preview",
      ],
    },
  },
};

const zh: RenewalEntryCopy = {
  pricing: {
    eyebrow: "按成果深度选择",
    title: "满意之后再开始",
    description:
      "所有产品都从免费预览开始。决定做成自己的产品后，再选择范围并当场查看准确价格。",
    scopeNote: "Preview 免费。Ready 每月 US$9 起，Tailored 每月 US$29 起；模型用量和最终范围会在同意前显示。",
    tiers: [
      {
        name: "Preview",
        promise: "永久免费",
        description: "查看预览候选，并确认是否适合自己的资料。无需注册。",
        action: "打开预览",
      },
      {
        name: "Ready",
        promise: "将预览候选调整为你的产品",
        description:
          "把品牌和资料应用到已查看的候选产品，发布前再次检查所需验证证据。",
        action: "查看产品",
      },
      {
        name: "Tailored",
        promise: "贴合你的业务",
        description: "调整部分数据、规则和界面，在批准前先查看影响范围。",
        action: "选择范围",
        badge: "多数团队从这里开始",
      },
      {
        name: "Autopilot",
        promise: "持续运行",
        description: "coogo 负责发布后的运营、变更与改进，你只需确认重要范围。",
        action: "委托运营",
        badge: "即将开放",
      },
    ],
    range: {
      eyebrow: "已认证的范围选择",
      title: "选择运营范围和支付周期",
      description:
        "从这里开始，实际金额仅向已登录账户显示，并以同一金额进入同意和结账。",
      loading: "正在确认金额…",
      monthly: "按月开始",
      yearly: "按年开始",
      close: "关闭范围",
      unavailable: "目前没有可选的付费范围。",
    },
    questions: [
      { question: "预览需要付费吗？", answer: "不需要。查看成果始终免费。" },
      {
        question: "开始后金额会增加吗？",
        answer:
          "同意前会再次确认范围和周期。除非你主动改变范围，否则不会自行增加。",
      },
      {
        question: "Showroom 之外的产品也可以吗？",
        answer: "可以。描述所需成果，我们会在 Tailored 范围内共同设计。",
      },
    ],
  },
  docs: {
    searchPlaceholder: "你想完成什么？",
    searchLabel: "按成果搜索",
    groups: [
      {
        title: "开始",
        links: ["5分钟打开第一个产品", "导入你的资料", "用语言修改"],
      },
      { title: "运营", links: ["使用驾驶舱", "处理确认请求", "阅读航行报告"] },
      {
        title: "深入了解",
        links: ["一个语义模型", "验证与证据记录", "数据与安全", "API 与集成"],
      },
    ],
    kicker: "开始",
    title: "5分钟打开第一个产品",
    descriptionBefore: "这不是只用来阅读的文档。点击每一步的“",
    descriptionAction: "打开预览",
    descriptionAfter: "”，就会在引导式预览中打开该步骤。",
    steps: [
      {
        title: "在 Showroom 选择成果",
        description:
          "按想要的成果而不是行业查找。每个已发布条目都会显示引导式预览与证据状态。",
        action: "打开预览",
      },
      {
        title: "体验客户与运营者两侧",
        description: "切换两侧查看完整流程。免费 Preview 无需注册。",
        action: "打开预览",
      },
      {
        title: "导入资料并用语言修改",
        description: "导入 CSV、Notion 或文档，再用一句话说明想要的成果。",
        command: "为新会员创建第一周欢迎旅程",
        followUp: "coogo 会先显示受影响范围，只有确认后才会应用。",
      },
      {
        title: "确认并发布",
        description:
          "App、Design、Guide 和 Insights 会一起准备。验证后只发布你批准的范围。",
        action: "打开 Studio",
      },
    ],
    nextLabel: "下一篇",
    nextTitle: "驾驶舱使用方法",
    nextDescription: "了解发布后 coogo 如何继续运营。",
    nextAction: "下一步",
    onThisPage: "本页内容",
    toc: ["选择成果", "体验两侧", "用语言修改", "确认并发布"],
    noResults: "暂时没有符合该目标的文档。",
  },
  auth: {
    loginTitle: "返回驾驶舱",
    loginDescription: "你离开期间，产品仍在持续运行。",
    signupTitle: "打开新的驾驶舱",
    signupDescription: "把第一个请求和资料连接成能够持续运营的成果。",
    pendingLabel: "有一个请求正在等待",
    pendingDescription: "登录后会在同一工作页面继续该请求。",
    savedRequest: "已保存的请求",
    google: "使用 Google 继续",
    github: "使用 GitHub 继续",
    or: "或者",
    email: "电子邮箱",
    password: "密码",
    rotateTitle: "请设置您自己的密码",
    rotateDescription:
      "该账户仍在使用安装时由设备签发的密码。请更换后继续。",
    rotateNewPassword: "新密码",
    rotateConfirmPassword: "确认新密码",
    rotateMismatch: "两次输入的密码不一致。",
    rotateSubmit: "保存并继续",
    processing: "处理中…",
    continueEmail: "使用邮箱继续",
    createAccount: "创建账户",
    newQuestion: "第一次使用？",
    existingQuestion: "已有账户？",
    tryFirst: "无需注册，先打开预览",
    signIn: "登录",
    accountNote: "保存成果时再创建账户。",
    report: {
      kicker: "预览 — 运营报告示例",
      beforePrimary: "连接实测验证前，",
      primary: "运营指标",
      between: "和",
      secondary: "验证状态",
      afterSecondary: "不会显示。连接后只展示真实记录。",
      approve: "继续预览",
      review: "查看证据规则",
      replyPlaceholder: "输入回复…",
      metrics: [
        "客户指标 · 等待连接",
        "验证 receipt · 等待连接",
        "site · emails · journey — preview",
      ],
    },
  },
};

const zhTW: RenewalEntryCopy = {
  pricing: {
    eyebrow: "依成果深度選擇",
    title: "滿意之後再開始",
    description:
      "所有產品都從免費預覽開始。決定做成自己的產品後，再選擇範圍並當場查看準確價格。",
    scopeNote: "Preview 免費。Ready 每月 US$9 起，Tailored 每月 US$29 起；模型用量和最終範圍會在同意前顯示。",
    tiers: [
      {
        name: "Preview",
        promise: "永遠免費",
        description: "查看預覽候選，並確認是否適合自己的資料。無需註冊。",
        action: "開啟預覽",
      },
      {
        name: "Ready",
        promise: "將預覽候選調整為你的產品",
        description:
          "把品牌和資料套用到已查看的候選產品，發佈前再次檢查所需驗證證據。",
        action: "查看產品",
      },
      {
        name: "Tailored",
        promise: "貼合你的業務",
        description: "調整部分資料、規則和畫面，在核准前先查看影響範圍。",
        action: "選擇範圍",
        badge: "多數團隊從這裡開始",
      },
      {
        name: "Autopilot",
        promise: "持續運作",
        description: "coogo 負責發佈後的營運、變更與改進，你只需確認重要範圍。",
        action: "委託營運",
        badge: "即將開放",
      },
    ],
    range: {
      eyebrow: "已驗證的範圍選擇",
      title: "選擇營運範圍與付款週期",
      description:
        "從這裡開始，實際金額只向已登入帳戶顯示，並以相同金額進入同意與結帳。",
      loading: "正在確認金額…",
      monthly: "按月開始",
      yearly: "按年開始",
      close: "關閉範圍",
      unavailable: "目前沒有可選的付費範圍。",
    },
    questions: [
      { question: "預覽需要付費嗎？", answer: "不需要。查看成果永遠免費。" },
      {
        question: "開始後金額會增加嗎？",
        answer:
          "同意前會再次確認範圍與週期。除非你主動改變範圍，否則不會自行增加。",
      },
      {
        question: "Showroom 之外的產品也可以嗎？",
        answer: "可以。描述所需成果，我們會在 Tailored 範圍內共同設計。",
      },
    ],
  },
  docs: {
    searchPlaceholder: "你想完成什麼？",
    searchLabel: "依成果搜尋",
    groups: [
      {
        title: "開始",
        links: ["5分鐘開啟第一個產品", "匯入你的資料", "用語言修改"],
      },
      { title: "營運", links: ["使用駕駛艙", "處理確認請求", "閱讀航行報告"] },
      {
        title: "深入了解",
        links: ["一個語意模型", "驗證與證據記錄", "資料與安全", "API 與整合"],
      },
    ],
    kicker: "開始",
    title: "5分鐘開啟第一個產品",
    descriptionBefore: "這不是只用來閱讀的文件。點擊每一步的「",
    descriptionAction: "開啟預覽",
    descriptionAfter: "」，就會在引導式預覽中開啟該步驟。",
    steps: [
      {
        title: "在 Showroom 選擇成果",
        description:
          "依想要的成果而不是產業尋找。每個已發佈項目都會顯示引導式預覽與證據狀態。",
        action: "開啟預覽",
      },
      {
        title: "體驗客戶與營運者兩側",
        description: "切換兩側查看完整流程。免費 Preview 無需註冊。",
        action: "開啟預覽",
      },
      {
        title: "匯入資料並用語言修改",
        description: "匯入 CSV、Notion 或文件，再用一句話說明想要的成果。",
        command: "為新會員建立第一週歡迎旅程",
        followUp: "coogo 會先顯示受影響範圍，只有確認後才會套用。",
      },
      {
        title: "確認並發佈",
        description:
          "App、Design、Guide 與 Insights 會一起準備。驗證後只發佈你核准的範圍。",
        action: "開啟 Studio",
      },
    ],
    nextLabel: "下一篇",
    nextTitle: "駕駛艙使用方法",
    nextDescription: "了解發佈後 coogo 如何繼續營運。",
    nextAction: "下一步",
    onThisPage: "本頁內容",
    toc: ["選擇成果", "體驗兩側", "用語言修改", "確認並發佈"],
    noResults: "暫時沒有符合該目標的文件。",
  },
  auth: {
    loginTitle: "返回駕駛艙",
    loginDescription: "你離開期間，產品仍在持續運作。",
    signupTitle: "開啟新的駕駛艙",
    signupDescription: "把第一個請求和資料連結成能夠持續營運的成果。",
    pendingLabel: "有一個請求正在等待",
    pendingDescription: "登入後會在同一工作頁面繼續該請求。",
    savedRequest: "已儲存的請求",
    google: "使用 Google 繼續",
    github: "使用 GitHub 繼續",
    or: "或者",
    email: "電子郵件",
    password: "密碼",
    rotateTitle: "請設定您自己的密碼",
    rotateDescription:
      "此帳號仍在使用安裝時由設備簽發的密碼。請更換後繼續。",
    rotateNewPassword: "新密碼",
    rotateConfirmPassword: "確認新密碼",
    rotateMismatch: "兩次輸入的密碼不一致。",
    rotateSubmit: "儲存並繼續",
    processing: "處理中…",
    continueEmail: "使用電子郵件繼續",
    createAccount: "建立帳戶",
    newQuestion: "第一次使用？",
    existingQuestion: "已有帳戶？",
    tryFirst: "無需註冊，先開啟預覽",
    signIn: "登入",
    accountNote: "儲存成果時再建立帳戶。",
    report: {
      kicker: "預覽 — 營運報告範例",
      beforePrimary: "連接實測驗證前，",
      primary: "營運指標",
      between: "和",
      secondary: "驗證狀態",
      afterSecondary: "不會顯示。連接後只展示真實記錄。",
      approve: "繼續預覽",
      review: "查看證據規則",
      replyPlaceholder: "輸入回覆…",
      metrics: [
        "客戶指標 · 等待連接",
        "驗證 receipt · 等待連接",
        "site · emails · journey — preview",
      ],
    },
  },
};

export const RENEWAL_ENTRY_COPY: Record<LocaleCode, RenewalEntryCopy> = {
  en,
  ko,
  ja,
  zh,
  "zh-TW": zhTW,
};

export function getRenewalEntryCopy(locale: LocaleCode): RenewalEntryCopy {
  return RENEWAL_ENTRY_COPY[locale] ?? RENEWAL_ENTRY_COPY.en;
}
